import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Modal, Row, Typography } from 'antd';
import { useExpirationClock } from '@identity/hooks';
import moment from 'moment';
import { useMutation, useQuery } from '@common/hooks';
import { redirectToLogin } from 'works/legacy/lib/redirect-to-login';
import Auth from 'works/legacy/lib/auth';
import Filters from 'works/legacy/lib/filters';
import {
  SignOutMutation,
  SignOutMutationResponse,
  TouchTokenQuery,
  TouchTokenQueryResponse,
} from '@identity/graphql';

const { Text } = Typography;

export function InactivityModal() {
  const [visible, setVisible] = useState(false);
  const { loading, data, fetchMore } =
    useQuery<TouchTokenQueryResponse>(TouchTokenQuery);

  const [runSignOutMutation, { loading: loadingSignOut }] =
    useMutation<SignOutMutationResponse>(SignOutMutation, {
      update() {
        Auth.signOut();
        Filters.clearFilters();
      },
    });

  const {
    authenticationIdleTimeoutInSeconds,
    authenticationIdleTimeoutInText,
  } = data?.currentMembership || {};

  useEffect(() => {
    Auth.update({
      idleTimeoutDuration: (authenticationIdleTimeoutInSeconds || 0) * 1000,
    });
  }, [authenticationIdleTimeoutInSeconds]);

  const { isExpired, isAboutToExpire, timeToExpire } = useExpirationClock(
    authenticationIdleTimeoutInSeconds
  );

  const handleSignOut = useCallback(async () => {
    if (!isExpired) {
      await runSignOutMutation();
    }
    redirectToLogin();
  }, [isExpired, runSignOutMutation]);

  // The isAboutToExpire takes 1 second to change.
  // This way we can close the modal without waiting for it.
  useEffect(() => {
    if (isAboutToExpire) setVisible(true);
  }, [isAboutToExpire]);

  // Automatically log out when session expires
  useEffect(() => {
    if (isExpired) {
      handleSignOut();
    }
  }, [isExpired, handleSignOut]);

  const handleStayOnline = async () => {
    // Prevent staying online if session has already expired
    if (isExpired) {
      handleSignOut();
      return;
    }

    await fetchMore({
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousQueryResult;

        return fetchMoreResult;
      },
    });
    setVisible(false);
  };

  return (
    <>
      <Modal
        className="inactivity-modal"
        visible={visible}
        title="Session Expiring Soon"
        footer={[
          <Button
            key="notActive"
            onClick={handleSignOut}
            loading={loadingSignOut}
          >
            Sign Out
          </Button>,
          <Button
            key="active"
            type="primary"
            onClick={handleStayOnline}
            disabled={isExpired}
            loading={loading}
          >
            Stay Signed In
          </Button>,
        ]}
        onCancel={isExpired ? undefined : handleStayOnline}
        closable={!isExpired}
        destroyOnClose
      >
        <Row>
          <Col>
            <Text>You&apos;ll be automatically signed out in:</Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Text className="duration">
              {moment
                .utc(moment.duration(timeToExpire).asMilliseconds())
                .format('mm:ss')}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Text>
              For your security, Works automatically signs you out after{' '}
              {authenticationIdleTimeoutInText} of inactivity.
            </Text>
          </Col>
        </Row>
      </Modal>
      <style jsx>{`
        :global(.inactivity-modal .ant-modal-footer) {
          text-align: right;
        }
        :global(.inactivity-modal .duration) {
          font-size: 24px;
          font-weight: 900;
          line-height: 64px;
        }
      `}</style>
    </>
  );
}
